import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { quelleText } from '../../components/planungsphase/antwortQuelle';
import {
    ADRESSAT_LABEL,
    KAPITEL,
    KAPITEL_TITEL,
    KRITIKALITAET_LABEL,
} from '../../components/planungsphase/fragenkatalog';
import {
    ampelFuerFrage,
    fristAbgelaufenText,
    gruppiereNachKapitel,
    heuteLokalIso,
    pflichtStandAus,
    sortiereFragen,
    STATUS_LABEL,
} from '../../components/planungsphase/planungLogic';
import type {
    FrageAdressat,
    PflichtStand,
    Planungsfrage,
    SchwellenwertErgebnis,
} from '../../components/planungsphase/types';
import {
    A4_GEOMETRY,
    cityFromAddress,
    CompanyInfo,
    createLetterhead,
    createSpaceGuard,
    drawFooters,
    drawPage1Header,
    FOOTER_RESERVE,
    longDate,
} from './letterhead';
import { COLORS, formatDate } from './pdfUtils';

/**
 * Die Klaerungsliste der Planungsphase als Geschaeftsbrief.
 *
 * Das Blatt hat zwei Gesichter, gesteuert ueber `mitAntworten`:
 *   - `false` (Standard) — das Blatt, das an den Bauherrn oder den Planer
 *     GEHT. Unter jeder Frage stehen leere Linien; wer keinen Platz zum
 *     Antworten hat, antwortet nicht.
 *   - `true` — der interne Stand mit den eingegangenen Antworten und ihrer
 *     Quelle (wer, Weg, Beleg, Eingang — `quelleText`). Die
 *     interne `bewertung` der Frage steht bewusst NIE auf dem Blatt: sie ist
 *     die Einschaetzung des Koordinators und nicht fuer den Empfaenger
 *     bestimmt (siehe Kommentar an `Planungsfrage.bewertung`).
 *
 * ─── Der Code ist die Referenz ────────────────────────────────────────────
 * `PL-C-04` steht in der Frageliste UND in der Uebersichtstabelle. Der
 * Empfaenger antwortet per Mail „zu PL-C-04: ..." — ohne den Code auf beiden
 * Seiten des Blattes ist der Ruecklauf nicht zuzuordnen, und genau daran
 * scheitern solche Kataloge in der Praxis.
 *
 * ─── Briefkopf ────────────────────────────────────────────────────────────
 * Ausschliesslich ueber `letterhead.ts` (createLetterhead / drawPage1Header /
 * drawFooters) — dieselbe Implementierung wie Rechnung, Angebot und
 * Einweisung. Umgeblaettert wird nur ueber `lh.addPage()`, damit ein
 * hinterlegtes Briefpapier auf JEDER Seite landet; die Uebersichtstabelle
 * bekommt zusaetzlich `willDrawPage` mit, weil autoTable seine Folgeseiten
 * selbst anlegt.
 *
 * ─── Umbruch ──────────────────────────────────────────────────────────────
 * Ein Frageblock (Kopfzeile, Fragetext, Rechtsgrundlage, Antwort bzw.
 * Antwortlinien) wird als EINE Einheit gesetzt: eine Frage, deren Antwortraum
 * auf der naechsten Seite steht, wird auf dem Papier nicht beantwortet.
 * Passt der Block selbst auf eine leere Seite nicht, wird er zeilenweise
 * umbrochen — abgeschnitten wird nichts.
 */

// ─── Typografie ─────────────────────────────────────────────────────────────

const SCHRIFT = {
    titel: 15,
    untertitel: 10.5,
    kapitel: 11,
    block: 9.5,
    klein: 8,
};

const ZEILE = {
    text: 4.4,
    klein: 3.4,
    kapitel: 5.4,
    /** Hoehe einer leeren Antwortlinie in mm — Platz fuer eine Handschrift. */
    antwort: 7,
};

/** Einzug des Fragetextes unter der Kopfzeile in mm. */
const EINZUG = 4;

/**
 * Feste Spaltenbreiten der Uebersichtstabelle in mm.
 *
 * Genau EINE Spalte ist 'auto' (die Frage) — sie bekommt, was uebrig bleibt.
 * Waeren es zwei, verteilte autoTable den Rest nach Inhalt und die Tabelle
 * saehe von Blatt zu Blatt anders aus.
 */
const FESTE_BREITEN = {
    code: 22,
    adressat: 26,
    kritikalitaet: 14,
    status: 30,
    frist: 22,
};

/**
 * Alle Spaltenbreiten der Uebersichtstabelle inklusive der berechneten
 * 'auto'-Spalte. Exportiert, damit die Summe pruefbar ist statt nur optisch
 * zu stimmen: sie MUSS `contentWidth` (170 mm) ergeben.
 */
export const UEBERSICHT_BREITEN = {
    ...FESTE_BREITEN,
    frage:
        A4_GEOMETRY.contentWidth -
        (FESTE_BREITEN.code +
            FESTE_BREITEN.adressat +
            FESTE_BREITEN.kritikalitaet +
            FESTE_BREITEN.status +
            FESTE_BREITEN.frist),
};

/** Ueberschrift des Blattes — auch fuer Dateinamen und Betreffzeilen. */
export const PLANUNGSFRAGEN_TITEL = 'Klärungsliste Planungsphase';

/** Ueberschrift der abschliessenden Tabelle. */
const UEBERSICHT_TITEL = 'Stand der Klärung';

// ─── Kleinkram ──────────────────────────────────────────────────────────────

/** Mehrzeiliges Feld zu einer Zeile — Kopfangaben sind einzeilig. */
const einzeilig = (v?: string | null): string =>
    (v == null ? '' : String(v)).replace(/\s*\n\s*/g, ', ').replace(/\s+/g, ' ').trim();

/** Kuerzel der Adressaten einer Frage, z. B. "BH, OP". Ein fehlender Wert
 *  wird benannt, nicht als Strich gedruckt. */
const adressatKuerzel = (frage: Planungsfrage): string => {
    const liste = (frage && frage.adressat ? frage.adressat : []).filter(Boolean);
    return liste.length > 0 ? liste.join(', ') : 'ohne Adressat';
};

/**
 * Wortlaut der drei Pflichten. „Nein" steht nur, wenn die Pflicht sicher
 * nicht greift — eine noch ungeklaerte Pflicht stand hier bisher als „Nein",
 * und jeder spaetere Leser las daraus eine Entwarnung.
 */
const PFLICHT_WORT: Record<PflichtStand, string> = {
    pflicht: 'Ja',
    keine: 'Nein',
    ungeklaert: 'noch nicht geklärt',
};

export interface PlanungsfragenPdfOptionen {
    projektName?: string;
    projektNummer?: string;
    bauvorhabenAdresse?: string;
    /** Wenn gesetzt: Blatt ist an EINEN Empfaenger gerichtet. */
    empfaengerName?: string;
    empfaengerAnschrift?: string[];
    /** Anschreiben-Absatz ueber der Liste. */
    anschreiben?: string;
    /** Ort/Datum-Zeile rechts. */
    ort?: string;
    datumIso?: string;
    /** Fuer die Ampel/Ueberfaelligkeit. */
    heuteIso?: string;
    /** Stand/Fassung, erscheint in der Kopfzeile der Liste. */
    stand?: string;
    /** Schwellenwert-Ergebnis; wenn gesetzt, kommt ein eigener Block davor. */
    pruefung?: SchwellenwertErgebnis | null;
    /** Baubeschreibung; wenn gesetzt, als Fliesstext-Block. */
    baubeschreibung?: string;
    /** Antworten mitdrucken (interner Stand) oder Leerzeilen zum Ausfuellen
     *  (Versand an den Bauherrn). Standard: false = Leerzeilen. */
    mitAntworten?: boolean;
    /** Nur mit `mitAntworten`: Titel der Belege (`project_documents.id` →
     *  Titel) fuer die Quellenzeile. Fehlt er, steht „Beleg in der Ablage". */
    belegTitel?: Record<string, string>;
    /**
     * Eigenes jsPDF-Objekt statt eines frisch erzeugten.
     *
     * Nur fuer Tests: nur so laesst sich pruefen, dass das Briefpapier auf
     * JEDER Seite genau einmal gestempelt wird. Dasselbe Muster wie
     * `__docFactory` im Rechnungs- und im Einweisungsgenerator.
     */
    __docFactory?: () => jsPDF;
}

/** Eine gesetzte Zeile eines Blocks — Text ODER eine leere Antwortlinie. */
interface Satzzeile {
    text: string;
    stil: 'normal' | 'bold' | 'italic';
    groesse: number;
    hoehe: number;
    einzug: number;
    /** Statt Text eine duenne Linie ueber die Satzbreite. */
    linie?: boolean;
}

// ─── Der Satz ───────────────────────────────────────────────────────────────

const renderPlanungsfragen = (
    doc: jsPDF,
    fragen: Planungsfrage[],
    company: CompanyInfo,
    optionen?: PlanungsfragenPdfOptionen,
): void => {
    const opt: PlanungsfragenPdfOptionen = optionen || {};
    const geo = A4_GEOMETRY;
    const { margin, contentWidth, rightX } = geo;

    const [tp0, tp1, tp2] = COLORS.text.primary;
    const [ln0, ln1, ln2] = COLORS.section.line;

    const liste: Planungsfrage[] = (fragen || []).filter(Boolean);
    const heuteIso = (opt.heuteIso || '').slice(0, 10) || heuteLokalIso();
    const mitAntworten = opt.mitAntworten === true;

    /**
     * Fristangabe einer Frage — fuer Statuszeile und Uebersichtstabelle.
     *
     * Eine verstrichene Frist steht ausgeschrieben als „Frist abgelaufen am …",
     * unabhaengig davon, ob der Status schon `nicht_beantwortet` ist: der
     * Ablauf ist ein Fakt aus `frist_am`, der Status nur die Feststellung
     * dazu. Eine laufende Frist steht als Datum (mit `praefix`), eine fehlende
     * wird benannt — nie als Strich gedruckt.
     */
    const fristAngabe = (frage: Planungsfrage, praefix: string, ohne: string): string => {
        const abgelaufen = fristAbgelaufenText(frage, heuteIso);
        if (abgelaufen) return abgelaufen;
        const datum = formatDate(frage.frist_am);
        return datum ? `${praefix}${datum}` : ohne;
    };

    const lh = createLetterhead(doc, company, geo);
    lh.stampCurrentBg();
    const { addressFieldY, rightColBottom } = drawPage1Header(doc, company, lh, geo);

    let y = 0;

    const ensureSpace = createSpaceGuard(
        lh,
        geo,
        () => y,
        (v) => {
            y = v;
        },
    );

    /**
     * Platz fuer einen Block der Hoehe `hoehe` — oder eine neue Seite.
     *
     * `createSpaceGuard` misst `needed` gegen die BLATTKANTE und klemmt nur
     * gegen den Fusszeilenstreifen ab. Ein mehrzeiliger Block muss seine
     * eigene Hoehe deshalb draufrechnen, sonst beginnt er zwar noch auf der
     * Seite, seine letzten Zeilen landen aber in der Fusszeile.
     */
    const fussReserve = lh.hasStationery ? lh.botMargin : FOOTER_RESERVE;
    const platzFuer = (hoehe: number) => ensureSpace(hoehe + fussReserve);

    /** Was auf einer frischen Seite ueberhaupt Platz hat. */
    const nutzbareHoehe = geo.pageHeight - lh.contentTopFollowing - fussReserve;

    // ── Anschriftfeld (nur wenn das Blatt an jemanden gerichtet ist) ──────
    let addrY = addressFieldY + 5;
    const empfName = (opt.empfaengerName || '').trim();
    const empfZeilen = (opt.empfaengerAnschrift || [])
        .map((z) => (z == null ? '' : String(z)).trim())
        .filter(Boolean);
    const hatEmpfaenger = !!empfName || empfZeilen.length > 0;
    if (hatEmpfaenger) {
        doc.setTextColor(tp0, tp1, tp2);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        const alleZeilen = (empfName ? [empfName] : []).concat(empfZeilen);
        alleZeilen.forEach((zeile) => {
            (doc.splitTextToSize(zeile, 85) as string[]).forEach((z) => {
                doc.text(z, margin, addrY);
                addrY += 5;
            });
        });
    }

    // ── Ort und Datum ────────────────────────────────────────────────────
    y = hatEmpfaenger
        ? Math.max(addrY + 6, addressFieldY + 30, rightColBottom + 8)
        : Math.max(addressFieldY, rightColBottom + 8);

    const ort = (opt.ort || '').trim() || cityFromAddress(company ? company.address : undefined);
    const datumText = longDate(opt.datumIso || heuteIso);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(SCHRIFT.untertitel);
    doc.setTextColor(tp0, tp1, tp2);
    doc.text(ort ? `${ort}, ${datumText}` : datumText, rightX, y, { align: 'right' });
    y += 12;

    // ── Titel ────────────────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(SCHRIFT.titel);
    doc.text(PLANUNGSFRAGEN_TITEL, margin, y);
    y += 7;

    // ── Untertitel: Projekt, Bauvorhaben, Stand ──────────────────────────
    const untertitel: string[] = [];
    const projektName = einzeilig(opt.projektName);
    const projektNummer = einzeilig(opt.projektNummer);
    if (projektName && projektNummer) untertitel.push(`${projektName} · Projekt-Nr. ${projektNummer}`);
    else if (projektName) untertitel.push(projektName);
    else if (projektNummer) untertitel.push(`Projekt-Nr. ${projektNummer}`);

    const bauvorhaben = einzeilig(opt.bauvorhabenAdresse);
    if (bauvorhaben) untertitel.push(`Bauvorhaben: ${bauvorhaben}`);

    const stand = einzeilig(opt.stand);
    if (stand) untertitel.push(`Stand: ${stand}`);

    if (untertitel.length > 0) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(SCHRIFT.untertitel);
        untertitel.forEach((zeile) => {
            (doc.splitTextToSize(zeile, contentWidth) as string[]).forEach((z) => {
                doc.text(z, margin, y);
                y += 5;
            });
        });
    }

    y += 1.5;
    doc.setDrawColor(ln0, ln1, ln2);
    doc.setLineWidth(0.2);
    doc.line(margin, y, rightX, y);
    y += 6;

    // ─────────────────────────────────────────────────────────────────────
    // Werkzeug: Zeilen bauen, Bloecke ungetrennt setzen
    // ─────────────────────────────────────────────────────────────────────

    const zeilenAus = (
        text: string,
        stil: 'normal' | 'bold' | 'italic',
        groesse: number,
        hoehe: number,
        einzug = 0,
    ): Satzzeile[] => {
        const roh = (text == null ? '' : String(text)).trim();
        if (!roh) return [];
        // splitTextToSize misst mit der GERADE gesetzten Schrift — deshalb
        // erst stellen, dann umbrechen.
        doc.setFont('helvetica', stil);
        doc.setFontSize(groesse);
        return (doc.splitTextToSize(roh, contentWidth - einzug) as string[]).map((z) => ({
            text: z,
            stil,
            groesse,
            hoehe,
            einzug,
        }));
    };

    const antwortLinie = (einzug = EINZUG): Satzzeile => ({
        text: '',
        stil: 'normal',
        groesse: SCHRIFT.block,
        hoehe: ZEILE.antwort,
        einzug,
        linie: true,
    });

    const zeichneZeile = (z: Satzzeile) => {
        if (z.linie) {
            doc.setDrawColor(ln0, ln1, ln2);
            doc.setLineWidth(0.15);
            doc.line(margin + z.einzug, y + 1, rightX, y + 1);
            y += z.hoehe;
            return;
        }
        doc.setFont('helvetica', z.stil);
        doc.setFontSize(z.groesse);
        doc.setTextColor(tp0, tp1, tp2);
        doc.text(z.text, margin + z.einzug, y);
        y += z.hoehe;
    };

    /**
     * Setzt einen Block als Einheit: passt er auf eine leere Seite, wird er
     * NIE zerrissen. Ist er laenger als eine Seite, bricht er zeilenweise um
     * — abgeschnitten wird nichts.
     */
    const zeichneBlock = (zeilen: Satzzeile[], nachAbstand = 0) => {
        const echte = (zeilen || []).filter(Boolean);
        if (echte.length === 0) return;
        const hoehe = echte.reduce((s, z) => s + z.hoehe, 0);
        if (hoehe <= nutzbareHoehe) {
            platzFuer(hoehe);
            echte.forEach(zeichneZeile);
        } else {
            echte.forEach((z) => {
                platzFuer(z.hoehe);
                zeichneZeile(z);
            });
        }
        y += nachAbstand;
    };

    // ── Anschreiben ──────────────────────────────────────────────────────
    const anschreiben = (opt.anschreiben || '').trim();
    if (anschreiben) {
        // Absaetze einzeln: ein Absatz darf umbrechen, aber die Leerzeile
        // zwischen zwei Absaetzen soll nicht auf der Folgeseite landen.
        anschreiben
            .replace(/\r\n/g, '\n')
            .split(/\n{2,}/)
            .map((a) => a.replace(/\s*\n\s*/g, ' ').trim())
            .filter(Boolean)
            .forEach((absatz) => {
                zeichneBlock(zeilenAus(absatz, 'normal', SCHRIFT.block, ZEILE.text), 3);
            });
        y += 2;
    }

    // ── Baubeschreibung ──────────────────────────────────────────────────
    const baubeschreibung = (opt.baubeschreibung || '').trim();
    if (baubeschreibung) {
        zeichneBlock(zeilenAus('Baubeschreibung', 'bold', SCHRIFT.kapitel, ZEILE.kapitel));
        baubeschreibung
            .replace(/\r\n/g, '\n')
            .split(/\n{2,}/)
            .map((a) => a.replace(/\s*\n\s*/g, ' ').trim())
            .filter(Boolean)
            .forEach((absatz) => {
                zeichneBlock(zeilenAus(absatz, 'normal', SCHRIFT.block, ZEILE.text), 2.5);
            });
        y += 3;
    }

    // ── Pruefung nach Baustellenverordnung ───────────────────────────────
    const pruefung = opt.pruefung;
    if (pruefung) {
        zeichneBlock(
            zeilenAus('Prüfung nach Baustellenverordnung', 'bold', SCHRIFT.kapitel, ZEILE.kapitel),
        );

        // Eingangsgroessen zuerst: ohne sie ist nicht nachvollziehbar, worauf
        // sich ein „Ja" stuetzt — und damit ist das Ergebnis wertlos.
        const grundlagen: string[] = [];
        if (Number.isFinite(pruefung.arbeitstage))
            grundlagen.push(`${pruefung.arbeitstage} Arbeitstage`);
        if (Number.isFinite(pruefung.maxBeschaeftigte))
            grundlagen.push(`max. ${pruefung.maxBeschaeftigte} Beschäftigte`);
        if (Number.isFinite(pruefung.personentage))
            grundlagen.push(`${pruefung.personentage} Personentage`);
        if (pruefung.mehrereArbeitgeber === true) grundlagen.push('mehrere Arbeitgeber');
        else if (pruefung.mehrereArbeitgeber === false) grundlagen.push('ein Arbeitgeber');
        if (grundlagen.length > 0) {
            zeichneBlock(
                zeilenAus(`Grundlage: ${grundlagen.join(', ')}`, 'italic', SCHRIFT.klein, ZEILE.klein),
                2,
            );
        }

        // Dreiwertig ueber `pflichtStandAus` — das rechnet alte Schnappschuesse
        // ohne `…Stand`-Felder zurueck, statt sie als „Nein" zu drucken.
        const zeilenPruefung: Array<[string, PflichtStand, string]> = [
            [
                'Vorankündigung (§ 2 Abs. 2 BaustellV)',
                pflichtStandAus(pruefung, 'vorankuendigung'),
                pruefung.vorankuendigungBegruendung,
            ],
            [
                'SiGe-Plan (§ 2 Abs. 3 BaustellV)',
                pflichtStandAus(pruefung, 'sigeplan'),
                pruefung.sigeplanBegruendung,
            ],
            [
                'Koordinator (§ 3 Abs. 1 BaustellV)',
                pflichtStandAus(pruefung, 'koordinator'),
                pruefung.koordinatorBegruendung,
            ],
        ];
        zeilenPruefung.forEach(([label, stand, begruendung]) => {
            const block = zeilenAus(
                `${label}: ${PFLICHT_WORT[stand]}`,
                'bold',
                SCHRIFT.block,
                ZEILE.text,
            ).concat(zeilenAus(begruendung, 'normal', SCHRIFT.block, ZEILE.text, EINZUG));
            zeichneBlock(block, 2);
        });

        const treffer = (pruefung.anhang2 || []).filter(Boolean);
        const zutreffend = treffer.filter((a) => a.zutreffend === true);
        const ungeklaert = treffer.filter((a) => a.zutreffend !== true && a.zutreffend !== false);
        if (zutreffend.length > 0) {
            const block = zeilenAus(
                'Zutreffende Arbeiten nach Anhang II BaustellV:',
                'bold',
                SCHRIFT.block,
                ZEILE.text,
            ).concat(
                zutreffend.flatMap((a) =>
                    zeilenAus(
                        `Nr. ${a.nummer} — ${(a.bezeichnung || '').trim()}`,
                        'normal',
                        SCHRIFT.block,
                        ZEILE.text,
                        EINZUG,
                    ),
                ),
            );
            zeichneBlock(block, 2);
        } else {
            zeichneBlock(
                zeilenAus(
                    'Keine der Arbeiten nach Anhang II BaustellV trifft zu.',
                    'normal',
                    SCHRIFT.block,
                    ZEILE.text,
                ),
                2,
            );
        }
        if (ungeklaert.length > 0) {
            // Ungeklaert ist NICHT ausgeschlossen — das muss auf dem Blatt
            // stehen, sonst liest der Empfaenger die Luecke als „trifft nicht zu".
            zeichneBlock(
                zeilenAus(
                    `Noch ungeklärt: Nr. ${ungeklaert.map((a) => a.nummer).join(', ')}`,
                    'normal',
                    SCHRIFT.block,
                    ZEILE.text,
                ),
                2,
            );
        }

        if (pruefung.unvollstaendig === true) {
            zeichneBlock(
                zeilenAus(
                    'Vorläufiges Ergebnis: Es fehlen Angaben. Die Prüfung ist erst belastbar, '
                        + 'wenn die offenen Fragen dieser Liste beantwortet sind.',
                    'bold',
                    SCHRIFT.block,
                    ZEILE.text,
                ),
                2,
            );
        }
        y += 4;
    }

    // ── Legende der Kuerzel ──────────────────────────────────────────────
    // Ohne sie ist die Kopfzeile jeder Frage ("PL-C-04 · A · BH, OP") fuer den
    // Empfaenger nicht lesbar — und genau diese Kuerzel stehen auch in der
    // Uebersichtstabelle, wo kein Platz fuer Klartext ist.
    if (liste.length > 0) {
        const vorkommend: FrageAdressat[] = [];
        liste.forEach((f) => {
            (f.adressat || []).forEach((a) => {
                if (a && vorkommend.indexOf(a) === -1) vorkommend.push(a);
            });
        });
        if (vorkommend.length > 0) {
            const text = vorkommend
                .map((a) => `${a} = ${ADRESSAT_LABEL[a] || a}`)
                .join('; ');
            zeichneBlock(zeilenAus(`Adressaten: ${text}`, 'italic', SCHRIFT.klein, ZEILE.klein), 1);
        }
        const kritText = (['A', 'B', 'C'] as const)
            .map((k) => `${k} = ${KRITIKALITAET_LABEL[k] || k}`)
            .join('; ');
        zeichneBlock(zeilenAus(`Kritikalität: ${kritText}`, 'italic', SCHRIFT.klein, ZEILE.klein), 5);
    }

    // ── Die Fragen, gruppiert nach Kapitel ───────────────────────────────
    const gruppen = gruppiereNachKapitel(liste);
    gruppen.forEach((gruppe) => {
        const kapitelInfo = KAPITEL.filter((k) => k && k.key === gruppe.kapitel)[0];
        const hinweis = kapitelInfo && kapitelInfo.hinweis ? kapitelInfo.hinweis : '';

        // Ueberschrift und Vorbemerkung gehoeren zusammen — und mindestens die
        // erste Zeile der ersten Frage muss mit drauf, sonst steht die
        // Ueberschrift allein am Seitenfuss.
        const kopf = zeilenAus(
            `${gruppe.kapitel} · ${KAPITEL_TITEL(gruppe.kapitel)}`,
            'bold',
            SCHRIFT.kapitel,
            ZEILE.kapitel,
        ).concat(zeilenAus(hinweis, 'italic', SCHRIFT.klein, ZEILE.klein));
        platzFuer(kopf.reduce((s, z) => s + z.hoehe, 0) + ZEILE.text * 2);
        zeichneBlock(kopf, 2.5);

        (gruppe.fragen || []).forEach((frage) => {
            if (!frage) return;
            const code = (frage.code || '').trim() || 'ohne Code';
            const krit = (frage.kritikalitaet || 'C') as 'A' | 'B' | 'C';

            let block = zeilenAus(
                `${code} · ${krit} · ${adressatKuerzel(frage)}`,
                'bold',
                SCHRIFT.block,
                ZEILE.text,
            );
            block = block.concat(
                zeilenAus(frage.frage, 'normal', SCHRIFT.block, ZEILE.text, EINZUG),
            );

            const recht = (frage.rechtsgrundlage || []).filter(Boolean);
            if (recht.length > 0) {
                block = block.concat(
                    zeilenAus(
                        `Rechtsgrundlage: ${recht.join('; ')}`,
                        'italic',
                        SCHRIFT.klein,
                        ZEILE.klein,
                        EINZUG,
                    ),
                );
            }

            if (mitAntworten) {
                const antwort = (frage.antwort || '').trim() || '— keine Antwort —';
                block = block.concat(
                    zeilenAus(`Antwort: ${antwort}`, 'normal', SCHRIFT.block, ZEILE.text, EINZUG),
                );
                // Quelle (wer, Weg, Beleg, Eingang) — dieselbe Zeile wie in der
                // Dokumentation; nur beim internen Stand.
                const belegId = frage.antwort_beleg_id || '';
                const quelle = quelleText(frage, belegId && opt.belegTitel ? opt.belegTitel[belegId] : undefined);
                if (quelle) {
                    block = block.concat(zeilenAus(quelle, 'italic', SCHRIFT.klein, ZEILE.klein, EINZUG));
                }
                const statusText = STATUS_LABEL[frage.status] || String(frage.status || '');
                const abgelaufen = fristAbgelaufenText(frage, heuteIso);
                // „überfällig" nur dort, wo der Fristablauf nicht ohnehin
                // dasteht — etwa als nicht beantwortet vermerkt, aber ohne Frist.
                const ueberfaellig = !abgelaufen && ampelFuerFrage(frage, heuteIso) === 'rot';
                block = block.concat(
                    zeilenAus(
                        `Status: ${statusText} · ${fristAngabe(frage, 'Frist: ', 'keine Frist gesetzt')}${ueberfaellig ? ' · überfällig' : ''}`,
                        'italic',
                        SCHRIFT.klein,
                        ZEILE.klein,
                        EINZUG,
                    ),
                );
            } else {
                // A-Fragen bekommen die dritte Linie: dort steht die Antwort,
                // an der die Freigabe der Planungsphase haengt.
                const linien = krit === 'A' ? 3 : 2;
                for (let i = 0; i < linien; i++) block = block.concat([antwortLinie()]);
            }

            zeichneBlock(block, 3.5);
        });

        y += 3;
    });

    // ── Uebersichtstabelle ───────────────────────────────────────────────
    if (liste.length > 0) {
        platzFuer(ZEILE.kapitel + 18);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SCHRIFT.kapitel);
        doc.setTextColor(tp0, tp1, tp2);
        doc.text(UEBERSICHT_TITEL, margin, y);
        y += 5;

        const sortiert = sortiereFragen(liste);
        /** Ueberfaellig je Zeile — Index = Zeilenindex der Tabelle. */
        const ueberfaellig = sortiert.map((f) => ampelFuerFrage(f, heuteIso) === 'rot');
        const body = sortiert.map((f) => [
            (f.code || '').trim() || 'ohne Code',
            (f.frage || '').trim(),
            adressatKuerzel(f),
            (f.kritikalitaet || '') as string,
            STATUS_LABEL[f.status] || String(f.status || ''),
            // Die Spalte heisst schon „Frist" — ein blosses Datum genuegt,
            // der Ablauf dagegen steht ausgeschrieben.
            fristAngabe(f, '', 'keine Frist'),
        ]);

        autoTable(doc, {
            startY: y,
            head: [['Code', 'Frage', 'Adressat', 'Krit.', 'Status', 'Frist']],
            body,
            theme: 'grid',
            // Ohne wiederholte Kopfzeile weiss der Leser auf Seite 4 nicht
            // mehr, welche Spalte die Frist ist.
            showHead: 'everyPage',
            margin: lh.autoTableMargin(),
            // autoTable legt seine Folgeseiten SELBST an — ohne den Callback
            // bliebe jede davon ohne Briefpapier.
            willDrawPage: lh.willDrawPage,
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [tp0, tp1, tp2],
                fontSize: SCHRIFT.klein,
                fontStyle: 'bold',
                lineColor: [ln0, ln1, ln2],
                lineWidth: 0.2,
            },
            styles: {
                fontSize: SCHRIFT.klein,
                textColor: [tp0, tp1, tp2],
                lineColor: [ln0, ln1, ln2],
                lineWidth: 0.2,
                cellPadding: { top: 1.4, bottom: 1.4, left: 1.6, right: 1.6 },
                valign: 'top',
                // Nichts wird abgeschnitten — ein halber Fragetext behauptet
                // etwas anderes als der ganze.
                overflow: 'linebreak',
            },
            columnStyles: {
                0: { cellWidth: FESTE_BREITEN.code },
                1: { cellWidth: 'auto' },
                2: { cellWidth: FESTE_BREITEN.adressat },
                3: { cellWidth: FESTE_BREITEN.kritikalitaet, halign: 'center' },
                4: { cellWidth: FESTE_BREITEN.status },
                5: { cellWidth: FESTE_BREITEN.frist },
            },
            didParseCell: (data) => {
                if (data.section !== 'body') return;
                // Ueberfaellig heisst: Frist abgelaufen ODER als nicht
                // beantwortet vermerkt. Diese Zeilen sind der Grund, warum das
                // Blatt ueberhaupt verschickt wird.
                if (ueberfaellig[data.row.index]) data.cell.styles.fontStyle = 'bold';
            },
        });
    }

    // Fusszeile und Seitenzahl zuletzt — vorher steht die Gesamtzahl nicht fest.
    drawFooters(doc, company, lh, geo);
};

/**
 * Die Klaerungsliste als jsPDF-Dokument.
 *
 * Gibt bewusst das Dokument zurueck und keine Bytes: der Aufrufer entscheidet,
 * ob er speichert, anhaengt oder in die Vorschau haengt.
 */
export const generatePlanungsfragenPdf = (
    fragen: Planungsfrage[],
    company: CompanyInfo,
    optionen?: PlanungsfragenPdfOptionen,
): jsPDF => {
    const doc =
        optionen && optionen.__docFactory
            ? optionen.__docFactory()
            : new jsPDF({ format: 'a4', unit: 'mm', compress: true });
    renderPlanungsfragen(doc, fragen, company || {}, optionen);
    return doc;
};
